import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_ACTION_COLUMN } from '@client/utils';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Input } from 'antd';
import { Package } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/admin-console/platform-content/products')({
    staticData: {
        permissions: ['admin_content:read'],
        ui: {
            icon: Package,
            title: 'Products & Services',
            subtitle: 'Manage products and services here.',
            menuDescription: 'Manage products and services',
        },
    },
    component: Products,
});

type Service = RouterOutputs['admin']['service']['listServices']['rows'][number];
type QueryParams = RouterInputs['admin']['service']['listServices'];
type OrderBy = NonNullable<QueryParams['orderBy']>;

const columnHelper = createColumnHelper<Service, OrderBy>();

function Products() {
    const navigate = useNavigate();
    const [queryParams, setQueryParams] = useState<QueryParams>({
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        orderBy: {
            id: 'desc',
        },
    });

    const { data: services, isLoading } = trpc.admin.service.listServices.useQuery(queryParams);

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => String(value),
            align: 'center',
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
        }),
        columnHelper.accessor('code', {
            id: 'code',
            header: 'Code',
            sortable: 'code',
        }),
        columnHelper.accessor('description', {
            id: 'description',
            header: 'Description',
        }),
        columnHelper.accessor('unitPrice', {
            id: 'unitPrice',
            header: 'Unit Price',
            sortable: 'unitPrice',
            cell: ({ value }) => `$${Number(value).toFixed(3)}`,
        }),
        columnHelper.actions({
            id: 'actions',
            cell: ({ row }) => (
                <Button.Edit.Link
                    to="/admin-console/platform-content/products/$serviceId"
                    params={{
                        serviceId: row.id,
                    }}
                />
            ),
            ...DEFAULT_ACTION_COLUMN,
        }),
    ] as const;

    const tableProps = usePrismaTable({
        data: services?.rows || [],
        total: services?.total || 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'bottom',
        },
    });

    return (
        <PageController route={Route}>
            <Outlet />
            <div className="mb-4 flex items-center justify-start">
                <Input.Search
                    placeholder="Search services"
                    allowClear
                    enterButton="Search"
                    size="large"
                    onSearch={(value) => {
                        setQueryParams((prev) => ({
                            ...prev,
                            where: value ? { name: { contains: value, mode: 'insensitive' } } : undefined,
                            offset: 0,
                        }));
                    }}
                    className="w-96"
                />
            </div>

            <PrismaTable
                {...tableProps}
                onRow={(record) => ({
                    className: 'cursor-pointer',
                    onClick: () => {
                        navigate({
                            to: '/admin-console/platform-content/products/$serviceId',
                            params: {
                                serviceId: record.id,
                            },
                        });
                    },
                })}
            />
        </PageController>
    );
}
